// @flow
import React from 'react';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import { withRouter } from 'react-router';
import { compose } from 'redux';
import urlJoin from 'url-join';
import classnames from 'classnames';
import { uniq } from 'lodash';

import {
  selectSupportedLocales,
  selectLocale,
  selectBaseUrl,
  selectTheme,
  selectFonts,
} from '../../selectors/client';

import { DEFAULT_LOCALE, IMAGE_OG_DEFAULT, UI_THEME_DARK, UI_THEME_LIGHT } from '../../constants';
import { defineMessages, injectIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';
import { FONTS } from '../../reducers/client/fonts';
import { getLanguage, getUrlInLocale } from '../../lib/locale-utils';

declare var __CAPACITOR__: boolean;
declare var __ELECTRON__: boolean;
declare var __SERVER__: boolean;

function getUrl() {
  if (__SERVER__) {
    return global.CURRENT_PAGE_URL;
  }

  return window.location.href;
}

const messages = defineMessages({
  defaultMetaTitle: {
    id: 'app.meta.title-2',
    defaultMessage: 'IDAGIO | The Ultimate Classical Music Streaming App',
  },
  defaultMetaDescription: {
    id: 'app.meta.description',
    defaultMessage:
      'The ultimate classical music collection in one app. Discover, browse and stream now on IDAGIO.',
  },
  defaultMetaKeywords: {
    id: 'app.meta.keywords',
    defaultMessage:
      'classical music app, classical music streaming, classical music collection, classical music streaming app',
  },
});

type OwnProps = {
  title?: string,
  description?: string,
  keywords?: string,
  ogTitle?: string,
  ogDescription?: string,
  ogImage?: string,
  ogType?: string,
  googleSchema?: Object,
  nextCursor?: string,
  prevCursor?: string,
  currentCursor?: string,
  canonicalRoute?: string,
};

type MapStateToProps = {
  alternativeLanguages: string[],
  currentLocale: string,
  defaultLang: string,
  baseUrl: string,
  fonts: Object,
  theme: string,
};

type Props = OwnProps & MapStateToProps & { location: Object, intl: IntlShape };

const Head = (props: Props) => {
  const { intl } = props;
  const finalTitle = props.title || intl.formatMessage(messages.defaultMetaTitle);
  const finalDescription = props.description || intl.formatMessage(messages.defaultMetaDescription);
  const finalKeywords = props.keywords || intl.formatMessage(messages.defaultMetaKeywords);
  const finalOgTitle = props.ogTitle || finalTitle;
  const finalOgDescription = props.ogDescription || finalDescription;
  const finalOgImage = props.ogImage || IMAGE_OG_DEFAULT;
  const finalOgType = props.ogType || 'website';

  const htmlClasses = classnames({
    'eesti-display--loaded': props.fonts[FONTS.EESTI_DISPLAY].loaded,
    'theme-dark': props.theme === UI_THEME_DARK,
    'theme-light': props.theme === UI_THEME_LIGHT,
    'is-electron': __ELECTRON__,
  });

  const getPaginationUrl = cursor => {
    const { pathname, search } = props.location;
    const encodedCursor = encodeURIComponent(cursor);
    let newPathname = `/${pathname}/pages/${encodedCursor}`;

    if (props.currentCursor) {
      const base = pathname.split(`/${encodeURIComponent(props.currentCursor)}`)[0];
      newPathname = `/${base}/${encodedCursor}`;
    }

    const lang = '/' + props.currentLocale.split('-')[0];
    return `${props.baseUrl}${lang === '/' + props.defaultLang ? '' : lang}${newPathname}${search}`;
  };

  const hasParams =
    !!props.location && !!props.location.query && !!Object.keys(props.location.query).length;

  const noIndex = !props.canonicalRoute || hasParams;

  return (
    <Helmet>
      <html className={htmlClasses} lang={props.currentLocale} />

      <title key="title">{finalTitle}</title>
      <meta key="og:title" property="og:title" content={finalOgTitle} />
      <meta key="twitter:title" name="twitter:title" content={finalOgTitle} />

      <meta key="description" name="description" content={finalDescription} />
      <meta key="og:description" property="og:description" content={finalOgDescription} />
      <meta key="twitter:description" name="twitter:description" content={finalOgDescription} />

      <meta property="keywords" content={finalKeywords} />

      <meta key="og:image" property="og:image" content={finalOgImage} />
      <meta key="og:image:width" property="og:image:width" content="1200" />
      <meta key="og:image:height" property="og:image:height" content="630" />
      <meta key="twitter:image" name="twitter:image" content={finalOgImage} />

      <meta key="og:type" property="og:type" content={finalOgType} />

      <meta name="twitter:card" content="summary" />
      <meta property="og:url" content={getUrl()} />
      {noIndex && <meta name="robots" content="noindex" />}
      {props.googleSchema && (
        <script type="application/ld+json">{JSON.stringify(props.googleSchema)}</script>
      )}
      {props.canonicalRoute &&
        props.alternativeLanguages.map((lang, i) => (
          <link
            key={i}
            href={urlJoin(props.baseUrl, getUrlInLocale(props.canonicalRoute, lang))}
            rel="alternate"
            hrefLang={lang}
          />
        ))}
      {props.canonicalRoute && (
        <link
          rel="alternate"
          hrefLang="x-default"
          href={urlJoin(props.baseUrl, getUrlInLocale(props.canonicalRoute, DEFAULT_LOCALE))}
        />
      )}
      {props.canonicalRoute && (
        <link
          href={urlJoin(props.baseUrl, getUrlInLocale(props.canonicalRoute, props.currentLocale))}
          rel="canonical"
        />
      )}
      {props.nextCursor && <link href={getPaginationUrl(props.nextCursor)} rel="next" />}
      {props.prevCursor && <link href={getPaginationUrl(props.prevCursor)} rel="prev" />}
    </Helmet>
  );
};

function mapStateToProps(state) {
  const supportedLocales = selectSupportedLocales(state);
  const currentLocale = selectLocale(state);
  return {
    currentLocale,
    defaultLang: DEFAULT_LOCALE.split('-')[0],
    baseUrl: selectBaseUrl(state),
    fonts: selectFonts(state),
    theme: selectTheme(state),
    alternativeLanguages: uniq(supportedLocales.map(getLanguage)),
  };
}

export default compose(withRouter, connect(mapStateToProps, {}), injectIntl)(Head);
